/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Whether a snapshot belongs to the usable current authorization.
 */
export const DirectoryStatus = {
  NeverSynced: "never_synced",
  Current: "current",
  Stale: "stale",
} as const;
/**
 * Whether a snapshot belongs to the usable current authorization.
 */
export type DirectoryStatus = OpenEnum<typeof DirectoryStatus>;

/**
 * Authorization status, independent of directory sync.
 */
export const SlackDirectoryConnectionStatus = {
  Connected: "connected",
  Disconnected: "disconnected",
  ReconnectRequired: "reconnect_required",
} as const;
/**
 * Authorization status, independent of directory sync.
 */
export type SlackDirectoryConnectionStatus = OpenEnum<
  typeof SlackDirectoryConnectionStatus
>;

/**
 * Latest workflow state. Unknown means progress could not be checked.
 */
export const SyncStatus = {
  Idle: "idle",
  Queued: "queued",
  Running: "running",
  Retrying: "retrying",
  Failed: "failed",
  Unknown: "unknown",
} as const;
/**
 * Latest workflow state. Unknown means progress could not be checked.
 */
export type SyncStatus = OpenEnum<typeof SyncStatus>;

/**
 * An organization Slack workspace authorization. Authorization does not indicate directory sync or invocation protection.
 */
export type SlackDirectoryConnection = {
  /**
   * Project whose managed assistant answers Speakeasy app mentions and direct messages in this workspace.
   */
  defaultProjectId?: string | undefined;
  /**
   * Whether a snapshot belongs to the usable current authorization.
   */
  directoryStatus: DirectoryStatus;
  /**
   * When credentials were removed.
   */
  disconnectedAt?: Date | undefined;
  /**
   * Connection generation used to reject stale changes.
   */
  generation: string;
  /**
   * Permissions granted by Slack.
   */
  grantedScopes: Array<string>;
  /**
   * Durable organization workspace connection ID.
   */
  id: string;
  /**
   * Bounded authorization failure code, when known.
   */
  lastErrorCode?: string | undefined;
  /**
   * Last complete directory publication, possibly from an earlier authorization.
   */
  lastFullSyncSucceededAt?: Date | undefined;
  /**
   * Most recent failed attempt; may precede a successful sync.
   */
  lastSyncFailedAt?: Date | undefined;
  /**
   * Start of the latest attempted sync.
   */
  lastSyncStartedAt?: Date | undefined;
  /**
   * Members observed in the last complete snapshot, including guests and bots.
   */
  memberCount: number;
  /**
   * Permissions Speakeasy asks for that this install has not granted. Reinstalling the app grants them. Without chat:write, approval requests do not reach approvers in Slack.
   */
  missingScopes: Array<string>;
  /**
   * Authorization status, independent of directory sync.
   */
  status: SlackDirectoryConnectionStatus;
  /**
   * Verified members fetched in the current attempt.
   */
  syncMembers?: number | undefined;
  /**
   * Completed pages in the current attempt.
   */
  syncPages?: number | undefined;
  /**
   * Bounded progress phase while syncing.
   */
  syncPhase?: string | undefined;
  /**
   * Latest workflow state. Unknown means progress could not be checked.
   */
  syncStatus: SyncStatus;
  /**
   * Last connection change timestamp.
   */
  updatedAt: Date;
  /**
   * Verified Slack workspace ID.
   */
  workspaceId: string;
  /**
   * Workspace display name from Slack.
   */
  workspaceName: string;
};

/** @internal */
export const DirectoryStatus$inboundSchema: z.ZodMiniType<
  DirectoryStatus,
  unknown
> = openEnums.inboundSchema(DirectoryStatus);

/** @internal */
export const SlackDirectoryConnectionStatus$inboundSchema: z.ZodMiniType<
  SlackDirectoryConnectionStatus,
  unknown
> = openEnums.inboundSchema(SlackDirectoryConnectionStatus);

/** @internal */
export const SyncStatus$inboundSchema: z.ZodMiniType<SyncStatus, unknown> =
  openEnums.inboundSchema(SyncStatus);

/** @internal */
export const SlackDirectoryConnection$inboundSchema: z.ZodMiniType<
  SlackDirectoryConnection,
  unknown
> = z.pipe(
  z.object({
    default_project_id: z.optional(z.string()),
    directory_status: DirectoryStatus$inboundSchema,
    disconnected_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    generation: z.string(),
    granted_scopes: z.array(z.string()),
    id: z.string(),
    last_error_code: z.optional(z.string()),
    last_full_sync_succeeded_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_sync_failed_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    last_sync_started_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    member_count: z.int(),
    missing_scopes: z.array(z.string()),
    status: SlackDirectoryConnectionStatus$inboundSchema,
    sync_members: z.optional(z.int()),
    sync_pages: z.optional(z.int()),
    sync_phase: z.optional(z.string()),
    sync_status: SyncStatus$inboundSchema,
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    workspace_id: z.string(),
    workspace_name: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "default_project_id": "defaultProjectId",
      "directory_status": "directoryStatus",
      "disconnected_at": "disconnectedAt",
      "granted_scopes": "grantedScopes",
      "last_error_code": "lastErrorCode",
      "last_full_sync_succeeded_at": "lastFullSyncSucceededAt",
      "last_sync_failed_at": "lastSyncFailedAt",
      "last_sync_started_at": "lastSyncStartedAt",
      "member_count": "memberCount",
      "missing_scopes": "missingScopes",
      "sync_members": "syncMembers",
      "sync_pages": "syncPages",
      "sync_phase": "syncPhase",
      "sync_status": "syncStatus",
      "updated_at": "updatedAt",
      "workspace_id": "workspaceId",
      "workspace_name": "workspaceName",
    });
  }),
);

export function slackDirectoryConnectionFromJSON(
  jsonString: string,
): SafeParseResult<SlackDirectoryConnection, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SlackDirectoryConnection$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SlackDirectoryConnection' from JSON`,
  );
}
